import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from "@exxatdesignux/ui/lib/persisted-state"

import type { GettingStartedSetupStep } from "@/components/onboarding"
import type { DashboardDemoMode } from "@/lib/evaluation-dashboard-context"
import type { EvaluationDashboardScenario } from "@/lib/evaluation-dashboard-scenario"
import { DEMO_CASE_MIDWAY_TERM } from "@/lib/mock/evaluation-dashboard"
import {
  evaluationSchedulePath,
  evaluationTemplatesPath,
} from "@/lib/evaluation-routes"
import { productPersistKey, type Product } from "@/stores/app-store"

const ONBOARDING_PROGRESS_KEY = productPersistKey("exxat-surveys", "onboarding-progress")
const ONBOARDING_PATH_KEY = productPersistKey("exxat-surveys", "onboarding-path")
const ONBOARDING_GUIDE_DISMISSED_KEY = productPersistKey("exxat-surveys", "onboarding-guide-dismissed")
const ONBOARDING_NEXT_TASKS_DISMISSED_KEY = productPersistKey(
  "exxat-surveys",
  "onboarding-next-tasks-dismissed",
)
const ONBOARDING_PROGRESS_VERSION = 2
const ONBOARDING_PATH_VERSION = 1
const ONBOARDING_GUIDE_DISMISSED_VERSION = 1
const ONBOARDING_NEXT_TASKS_DISMISSED_VERSION = 1

export type SurveysOnboardingPath = "prism" | "import" | "manual"

export type SurveysOnboardingSheetId =
  | "term-setup"
  | "courses"
  | "survey-setup"
  | "schedule"
  | "templates"
  | "prism-connect"
  | "confirm-term"
  | "import-upload"
  | "import-preview"

export type SurveysOnboardingSurveyMode = "import" | "manual"

export type SurveysOnboardingSetupStep = GettingStartedSetupStep & {
  sheetId: SurveysOnboardingSheetId
}

export function isSurveysOnboardingPath(value: unknown): value is SurveysOnboardingPath {
  return value === "prism" || value === "import" || value === "manual"
}

export function loadSurveysOnboardingPath(): SurveysOnboardingPath | null {
  const raw = getStorageItem(ONBOARDING_PATH_KEY)
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return null
    const version = (parsed as { v?: number }).v
    const data = (parsed as { d?: unknown }).d
    if (version !== ONBOARDING_PATH_VERSION || !isSurveysOnboardingPath(data)) return null
    return data
  } catch {
    return null
  }
}

export function saveSurveysOnboardingPath(path: SurveysOnboardingPath) {
  setStorageItem(
    ONBOARDING_PATH_KEY,
    JSON.stringify({ v: ONBOARDING_PATH_VERSION, d: path }),
  )
}

export function loadSurveysOnboardingCompleted(
  path: SurveysOnboardingPath,
): Set<number> {
  const raw = getStorageItem(ONBOARDING_PROGRESS_KEY)
  if (!raw) return new Set()

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return new Set()
    const version = (parsed as { v?: number }).v
    const data = (parsed as { d?: unknown }).d

    if (version === ONBOARDING_PROGRESS_VERSION && typeof data === "object" && data !== null) {
      const storedPath = (data as { path?: unknown }).path
      const indices = (data as { completed?: unknown }).completed
      if (storedPath === path && Array.isArray(indices)) {
        return new Set(indices.filter((index): index is number => typeof index === "number"))
      }
      return new Set()
    }

    if (version === 1 && Array.isArray(data)) {
      return new Set(data.filter((index): index is number => typeof index === "number"))
    }
  } catch {
    return new Set()
  }

  return new Set()
}

export function saveSurveysOnboardingCompleted(
  path: SurveysOnboardingPath,
  completed: Set<number>,
) {
  setStorageItem(
    ONBOARDING_PROGRESS_KEY,
    JSON.stringify({
      v: ONBOARDING_PROGRESS_VERSION,
      d: { path, completed: [...completed] },
    }),
  )
}

export function clearSurveysOnboardingProgress() {
  removeStorageItem(ONBOARDING_PROGRESS_KEY)
  removeStorageItem(ONBOARDING_PATH_KEY)
  removeStorageItem(ONBOARDING_GUIDE_DISMISSED_KEY)
  removeStorageItem(ONBOARDING_NEXT_TASKS_DISMISSED_KEY)
}

export function loadSurveysOnboardingGuideDismissed(): boolean {
  const raw = getStorageItem(ONBOARDING_GUIDE_DISMISSED_KEY)
  if (!raw) return false

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return false
    const version = (parsed as { v?: number }).v
    const data = (parsed as { d?: unknown }).d
    if (version !== ONBOARDING_GUIDE_DISMISSED_VERSION) return false
    return data === true
  } catch {
    return false
  }
}

export function saveSurveysOnboardingGuideDismissed(dismissed: boolean) {
  setStorageItem(
    ONBOARDING_GUIDE_DISMISSED_KEY,
    JSON.stringify({ v: ONBOARDING_GUIDE_DISMISSED_VERSION, d: dismissed }),
  )
}

function loadDismissedFlag(key: string, version: number): boolean {
  const raw = getStorageItem(key)
  if (!raw) return false

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return false
    if ((parsed as { v?: number }).v !== version) return false
    return (parsed as { d?: unknown }).d === true
  } catch {
    return false
  }
}

export function loadSurveysOnboardingNextTasksDismissed(): boolean {
  return loadDismissedFlag(
    ONBOARDING_NEXT_TASKS_DISMISSED_KEY,
    ONBOARDING_NEXT_TASKS_DISMISSED_VERSION,
  )
}

export function saveSurveysOnboardingNextTasksDismissed(dismissed: boolean) {
  setStorageItem(
    ONBOARDING_NEXT_TASKS_DISMISSED_KEY,
    JSON.stringify({ v: ONBOARDING_NEXT_TASKS_DISMISSED_VERSION, d: dismissed }),
  )
}

export type SurveysOnboardingNextTask = {
  title: string
  description: string
  iconClass: string
  actionLabel: string
  href: string
}

export function surveysOnboardingNextTasks(product: Product): SurveysOnboardingNextTask[] {
  const termId = DEMO_CASE_MIDWAY_TERM.id
  return [
    {
      title: "Finish draft schedules",
      description: "Open dates for courses still in draft before collection starts.",
      iconClass: "fa-calendar-clock",
      actionLabel: "Open schedule",
      href: evaluationSchedulePath(product, { termId, stages: "draft" }),
    },
    {
      title: "Review courses not set up",
      description: "Close gaps in coverage before the term goes live.",
      iconClass: "fa-book-open",
      actionLabel: "View courses",
      href: evaluationSchedulePath(product, { termId, stages: "notSetup" }),
    },
    {
      title: "Add another survey",
      description: "Duplicate or adapt your first template for another course type.",
      iconClass: "fa-clipboard-list",
      actionLabel: "Open templates",
      href: evaluationTemplatesPath(product),
    },
  ]
}

export function isSurveysOnboardingComplete(
  completed: Set<number>,
  stepCount: number,
): boolean {
  return stepCount > 0 && completed.size >= stepCount
}

/**
 * V1 Operations maturity while first-term setup runs.
 * setup (hidden) → guided (checklist + current term) → ready (next tasks + current term)
 * → collecting (live term only) → full (live + last closed + other terms).
 */
export type SurveysOnboardingOperationsStage =
  | "hidden"
  | "guided"
  | "ready"
  | "collecting"
  | "full"

export function surveysOnboardingSurveyStepIndex(
  steps: SurveysOnboardingSetupStep[],
): number {
  return steps.findIndex(step => step.sheetId === "survey-setup")
}

export function hasCompletedFirstSurveyStep(
  completed: Set<number>,
  steps: SurveysOnboardingSetupStep[],
): boolean {
  const surveyStepIndex = surveysOnboardingSurveyStepIndex(steps)
  return surveyStepIndex >= 0 && completed.has(surveyStepIndex)
}

export function resolveSurveysOnboardingOperationsStage(
  completed: Set<number>,
  steps: SurveysOnboardingSetupStep[],
  options: {
    checklistComplete: boolean
    setupDismissed: boolean
    nextTasksDismissed: boolean
    scenario: EvaluationDashboardScenario
  },
): SurveysOnboardingOperationsStage {
  if (options.scenario === "advanced") return "full"
  if (options.scenario === "midway") return "collecting"

  if (!hasCompletedFirstSurveyStep(completed, steps)) return "hidden"
  if (!options.checklistComplete || !options.setupDismissed) return "guided"
  if (!options.nextTasksDismissed) return "ready"
  return "collecting"
}

export function surveysOnboardingOperationsDemo(
  stage: SurveysOnboardingOperationsStage,
): DashboardDemoMode {
  if (stage === "guided") return "stateSetup"
  if (stage === "ready") return "stateReady"
  if (stage === "collecting") return "stateFirstLive"
  if (stage === "full") return null
  return "empty"
}

export function surveysOnboardingShowsCurrentTermOnly(
  stage: SurveysOnboardingOperationsStage,
): boolean {
  return stage === "guided" || stage === "ready" || stage === "collecting"
}

export const SURVEYS_ONBOARDING_PATH_TILES: {
  value: SurveysOnboardingPath
  label: string
  description: string
  iconClass: string
}[] = [
  {
    value: "prism",
    label: "Exxat Prism",
    description: "Import term and courses from Exxat Prism",
    iconClass: "fa-graduation-cap",
  },
  {
    value: "import",
    label: "Import",
    description: "Upload a CSV of courses and dates",
    iconClass: "fa-file-arrow-up",
  },
  {
    value: "manual",
    label: "Manual",
    description: "Enter term details step by step",
    iconClass: "fa-pen-to-square",
  },
]

export const SURVEYS_ONBOARDING_SURVEY_MODE_TILES: {
  value: SurveysOnboardingSurveyMode
  label: string
  description: string
  iconClass: string
}[] = [
  {
    value: "import",
    label: "Import survey",
    description: "Start from an existing template",
    iconClass: "fa-file-import",
  },
  {
    value: "manual",
    label: "Create manually",
    description: "Name your first survey for this term",
    iconClass: "fa-pen-to-square",
  },
]

const MANUAL_STEPS: SurveysOnboardingSetupStep[] = [
  {
    title: "Set up your first term",
    description: "Add term dates and academic year so evaluations can be scoped to the right window.",
    iconClass: "fa-calendar-range",
    actionLabel: "Set up term",
    sheetId: "term-setup",
  },
  {
    title: "Connect course offerings",
    description: "Select courses that will receive student and faculty evaluations.",
    iconClass: "fa-book-open",
    actionLabel: "View courses",
    sheetId: "courses",
  },
  {
    title: "First survey",
    description: "Import an evaluation template or create the first survey for this term.",
    iconClass: "fa-clipboard-list",
    actionLabel: "Set up survey",
    sheetId: "survey-setup",
  },
  {
    title: "Schedule evaluations",
    description: "Set open dates for each course before collection starts.",
    iconClass: "fa-calendar-clock",
    actionLabel: "Schedule surveys",
    sheetId: "schedule",
  },
]

const PRISM_STEPS: SurveysOnboardingSetupStep[] = [
  {
    title: "Import from Exxat Prism",
    description: "Connect your Prism program to pull in the term and course offerings.",
    iconClass: "fa-graduation-cap",
    actionLabel: "Import from Prism",
    sheetId: "term-setup",
  },
  {
    title: "Confirm synced term",
    description: "Review the term Prism pulled in before you schedule evaluations.",
    iconClass: "fa-calendar-check",
    actionLabel: "Review term",
    sheetId: "confirm-term",
  },
  {
    title: "Review course list",
    description: "Confirm which synced courses will receive evaluations this term.",
    iconClass: "fa-book-open",
    actionLabel: "Review courses",
    sheetId: "courses",
  },
  {
    title: "First survey",
    description: "Import an evaluation template or create the first survey for this term.",
    iconClass: "fa-clipboard-list",
    actionLabel: "Set up survey",
    sheetId: "survey-setup",
  },
  {
    title: "Schedule evaluations",
    description: "Set open dates for each course before collection starts.",
    iconClass: "fa-calendar-clock",
    actionLabel: "Schedule surveys",
    sheetId: "schedule",
  },
]

const IMPORT_STEPS: SurveysOnboardingSetupStep[] = [
  {
    title: "Set up your first term",
    description: "Upload a CSV with course numbers, names, and term dates.",
    iconClass: "fa-calendar-range",
    actionLabel: "Set up term",
    sheetId: "term-setup",
  },
  {
    title: "Validate import",
    description: "Review rows before they are added to this term.",
    iconClass: "fa-table-list",
    actionLabel: "Preview import",
    sheetId: "import-preview",
  },
  {
    title: "First survey",
    description: "Import an evaluation template or create the first survey for this term.",
    iconClass: "fa-clipboard-list",
    actionLabel: "Set up survey",
    sheetId: "survey-setup",
  },
  {
    title: "Schedule evaluations",
    description: "Set open dates for each imported course before collection starts.",
    iconClass: "fa-calendar-clock",
    actionLabel: "Schedule surveys",
    sheetId: "schedule",
  },
]

export function surveysDashboardOnboardingSteps(
  _product: Product,
  path: SurveysOnboardingPath,
): SurveysOnboardingSetupStep[] {
  if (path === "prism") return PRISM_STEPS
  if (path === "import") return IMPORT_STEPS
  return MANUAL_STEPS
}

export function surveysOnboardingPathLabel(path: SurveysOnboardingPath): string {
  return SURVEYS_ONBOARDING_PATH_TILES.find(tile => tile.value === path)?.label ?? "Manual"
}
